<template>
  <div>
    <div class="date-time-settings-tabs">
      <UITabs
        :value="activeTab"
        type="line"
        @update:value="(v: string) => (activeTab = v)"
      >
        <!-- ============ GENERAL TAB ============ -->
        <UITabPane name="general" :tab="t('common.dateTime.tabGeneral')">
          <!-- Collect type (frozen after create / reload) -->
          <div v-if="newDateTimeEnabled" class="form-group">
            <div class="flex space-x-3">
              <span
                class="switch-handle mb-2 block text-sm font-medium text-gray-700"
              >
                {{ t("common.dateTime.collectHeading") }}
                <UITooltip
                  v-if="disableCollectType"
                  width="280px"
                  trigger="hover"
                  placement="top"
                >
                  <template #trigger>
                    <InfoCircleIcon
                      class="h-4 w-4 text-gray-400"
                    ></InfoCircleIcon>
                  </template>
                  {{ t("common.dateTime.collectTypeLockedTooltip") }}
                </UITooltip>
              </span>
            </div>
            <UIButtonGroup id="collect-type-group" class="dt-segmented-control">
              <UIButton
                v-for="opt in collectOptions"
                :id="toButtonId('collect', opt.value)"
                :key="opt.value"
                class="dt-segmented-button"
                :class="{
                  'dt-segmented-button--active': collect === opt.value,
                }"
                type="default"
                size="large"
                :active="collect === opt.value"
                :disabled="disableCollectType"
                @click="updateCollectType(opt.value)"
              >
                {{ opt.label }}
              </UIButton>
            </UIButtonGroup>
          </div>

          <!-- Field description: only surveys provide it, like their other fields -->
          <slot name="description"></slot>

          <!-- Label -->
          <div class="form-group">
            <div class="flex space-x-3">
              <span
                class="switch-handle mb-1 block text-sm font-medium text-gray-700"
                >{{ t("common.label") }}</span
              >
            </div>
            <UIInput
              id="label"
              class="standard-input-ghl"
              :model-value="dateElement.label"
              :status="
                isCustomFieldError || visibleLabelError ? 'error' : undefined
              "
              @update:model-value="updateLabel"
              @blur="store.addToHistory()"
            ></UIInput>
            <div v-if="isCustomFieldError" class="mt-1 text-xs text-red-500">
              {{ t("common.customFieldExistsError") }}
            </div>
            <div
              v-else-if="visibleLabelError"
              class="mt-1 text-xs text-red-500"
            >
              {{ t("common.requiredField") }}
            </div>
          </div>

          <div class="form-group">
            <LabelAlignment
              :is-element-level="true"
              :label-alignment="dateElement.labelAlignment"
              @update:label-alignment="handleLabelAlignment"
            ></LabelAlignment>
          </div>

          <!-- Placeholder / short label -->
          <div class="form-group">
            <span
              class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
              >{{ t("common.placeholder") }}</span
            >
            <UIInput
              id="fieldPlaceholder"
              class="standard-input-ghl"
              :model-value="dateElement.placeholder"
              :placeholder="t('common.dateTime.placeholderTextPlaceholder')"
              @update:model-value="updatePlaceholder"
              @blur="store.addToHistory()"
            ></UIInput>
          </div>
          <div class="form-group">
            <span
              class="switch-handle mb-1 block text-sm font-medium text-gray-700"
              >{{ t("common.shortLabel") }}</span
            >
            <UIInput
              id="shortLabel"
              class="standard-input-ghl"
              :model-value="dateElement.shortLabel"
              :placeholder="t('common.dateTime.shortLabelPlaceholder')"
              @update:model-value="updateShortLabel"
              @blur="store.addToHistory()"
            ></UIInput>
          </div>
          <div class="form-group">
            <div
              class="switch-handle mb-1 block text-sm font-medium text-gray-700"
            >
              {{ t("common.queryKey") }}
              <UITooltip width="300px" trigger="hover" placement="top">
                <template #trigger>
                  <InfoCircleIcon
                    class="h-4 w-4 text-gray-400"
                  ></InfoCircleIcon>
                </template>
                {{ t("common.querykeyTooltipMessage") }}
              </UITooltip>
            </div>
            <UIInput
              id="fieldQueryKey"
              class="standard-input-ghl"
              :model-value="dateElement.hiddenFieldQueryKey"
              @update:model-value="updateHiddenQuery"
              @blur="store.addToHistory()"
            ></UIInput>
          </div>

          <div v-if="isForm" class="form-group">
            <div
              class="switch-handle mb-1 block text-sm font-medium text-gray-700"
            >
              {{ t("common.style.fieldWidth") }}
            </div>
            <UIInputGroup>
              <UIInputNumber
                id="fieldWidthKey"
                size="large"
                class="w-100"
                :min="1"
                :max="100"
                :show-button="false"
                :model-value="dateElement.fieldWidthPercentage"
                @update:model-value="updateFieldWidthPercentage"
                @blur="getFieldWidthPercentage()"
              ></UIInputNumber
              ><UIInputGroupLabel>%</UIInputGroupLabel>
            </UIInputGroup>
          </div>

          <div class="form-check-req-hid">
            <UICheckbox
              id="fieldRequired"
              :disabled="isRequiredFieldDisable"
              :checked="dateElement.required"
              @update:checked="updateRequired"
              >{{ t("common.required") }}</UICheckbox
            >
            <UICheckbox
              id="fieldCheckbox"
              :disabled="isHiddenFieldDisable"
              :checked="dateElement.hidden"
              @update:checked="updateHidden"
              >{{ t("common.hidden") }}</UICheckbox
            >
          </div>
          <div v-if="dateElement?.hidden" class="form-group hidden-field">
            <span
              class="hl-text-input-label mb-1 block text-sm font-medium text-gray-700"
              >{{ t("common.hiddenValue") }}</span
            >
            <UIInput
              id="fieldHiddenvalue"
              class="standard-input-ghl"
              :model-value="dateElement.hiddenFieldValue"
              @update:model-value="updateHiddenValue"
              @blur="store.addToHistory()"
            ></UIInput>
          </div>
          <!-- Advanced settings (Custom field name + Unique key) — supplied by the
             hosting sidebar via the #advanced slot so its shared handlers stay
             in the parent while rendering inside the General tab. -->
          <template v-if="$slots.advanced">
            <UIDivider></UIDivider>
            <slot name="advanced"></slot>
          </template>
        </UITabPane>

        <!-- ============ FIELD SETTINGS TAB ============ -->
        <UITabPane name="settings" :tab="t('common.dateTime.tabFieldSettings')">
          <div
            v-if="newDateTimeEnabled"
            class="dt-field-settings-banner-alert mb-4"
          >
            <UIAlert
              id="dtBanner"
              type="default"
              :closable="false"
              :bordered="true"
            >
              <template #content>
                <div class="dt-field-settings-banner">
                  <CalendarDateIcon
                    class="dt-field-settings-banner-icon"
                  ></CalendarDateIcon>
                  <span class="dt-field-settings-banner-text">
                    {{ t("common.dateTime.fieldSettingsBanner") }}
                  </span>
                </div>
              </template>
            </UIAlert>
          </div>

          <!-- date format + separator (DATE / DATE_TIME) -->
          <template v-if="showDateRange">
            <div id="date-format" class="form-group">
              <span
                class="switch-handle mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-700"
              >
                {{ t("common.date.dateFormat") }}
                <UITooltip width="300px" trigger="hover" placement="top">
                  <template #trigger>
                    <InfoCircleIcon
                      class="h-4 w-4 text-gray-400"
                    ></InfoCircleIcon>
                  </template>
                  {{ t("common.date.dateFormatTooltipMessage") }}
                </UITooltip>
              </span>
              <UIButtonGroup
                id="dt-date-format-group"
                class="dt-segmented-control"
              >
                <UIButton
                  v-for="format in DATE_TIME_FORMAT_ORDER"
                  :id="toButtonId('date-format', format)"
                  :key="format"
                  class="dt-segmented-button"
                  :class="{
                    'dt-segmented-button--active': selectedFormat === format,
                  }"
                  type="default"
                  size="large"
                  :active="selectedFormat === format"
                  @click="updateDateFormat(format)"
                >
                  {{ formatLabel(format) }}
                </UIButton>
              </UIButtonGroup>
            </div>
            <div id="date-separator" class="form-group">
              <span
                class="switch-handle mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-700"
              >
                {{ t("common.date.dateSeparator") }}
                <UITooltip width="300px" trigger="hover" placement="top">
                  <template #trigger>
                    <InfoCircleIcon
                      class="h-4 w-4 text-gray-400"
                    ></InfoCircleIcon>
                  </template>
                  {{ t("common.date.dateSeparatorTooltipMessage") }}
                </UITooltip>
              </span>
              <UIButtonGroup
                id="dt-date-separator-group"
                class="dt-segmented-control"
              >
                <UIButton
                  v-for="separator in separators"
                  :id="toButtonId('date-separator', separator)"
                  :key="separator"
                  class="dt-segmented-button"
                  :class="{
                    'dt-segmented-button--active':
                      selectedSeparator === separator,
                  }"
                  type="default"
                  size="large"
                  :active="selectedSeparator === separator"
                  @click="updateDateSeparator(separator)"
                >
                  {{ separator }}
                </UIButton>
              </UIButtonGroup>
            </div>
          </template>

          <!-- time format + default time (TIME / DATE_TIME) -->
          <template v-if="showTime">
            <div class="form-group">
              <span
                class="switch-handle mb-2 flex items-center gap-1.5 text-sm font-medium text-gray-700"
              >
                {{ t("common.dateTime.timeFormatLabel") }}
                <UITooltip width="280px" trigger="hover" placement="top">
                  <template #trigger>
                    <InfoCircleIcon
                      class="h-4 w-4 text-gray-400"
                    ></InfoCircleIcon>
                  </template>
                  {{ t("common.dateTime.timeFormatLabel") }}
                </UITooltip>
              </span>
              <UIButtonGroup
                id="dt-time-format-group"
                class="dt-segmented-control"
              >
                <UIButton
                  v-for="opt in timeFormatOptions"
                  :id="toButtonId('time-format', opt.value)"
                  :key="opt.value"
                  class="dt-segmented-button"
                  :class="{
                    'dt-segmented-button--active': timeFormat === opt.value,
                  }"
                  type="default"
                  size="large"
                  :active="timeFormat === opt.value"
                  @click="updateTimeFormat(opt.value)"
                >
                  {{ opt.label }}
                </UIButton>
              </UIButtonGroup>
            </div>
            <div class="form-group">
              <span
                class="switch-handle mb-2 block text-sm font-medium text-gray-700"
                >{{ t("common.dateTime.defaultTimeLabel") }}</span
              >
              <UIButtonGroup
                id="dt-default-time-group"
                class="dt-segmented-control"
              >
                <UIButton
                  v-for="opt in defaultTimeOptions"
                  :id="toButtonId('default-time', opt.value)"
                  :key="opt.value"
                  class="dt-segmented-button"
                  :class="{
                    'dt-segmented-button--active': defaultTime === opt.value,
                  }"
                  type="default"
                  size="large"
                  :active="defaultTime === opt.value"
                  @click="updateDefaultTime(opt.value)"
                >
                  {{ opt.label }}
                </UIButton>
              </UIButtonGroup>
              <div class="mt-2 text-xs leading-snug text-gray-500">
                {{ t("common.dateTime.defaultTimeHelper") }}
              </div>
            </div>
            <div v-if="defaultTime === 'custom'" class="form-group">
              <span
                class="switch-handle mb-1 block text-sm font-medium text-gray-700"
                >{{ t("common.dateTime.customTimeLabel") }}</span
              >
              <div class="dt-time-picker-row">
                <div class="dt-time-picker-shell">
                  <ClockIcon class="dt-time-picker-leading-icon"></ClockIcon>
                  <UITimePicker
                    id="customTime"
                    class="standard-input-ghl dt-time-picker"
                    :actions="null"
                    :format="timePickerFormat"
                    :hours="timePickerHours"
                    :placeholder="timePickerPlaceholder"
                    :show-icon="false"
                    :status="visibleCustomTimeError ? 'error' : undefined"
                    :to="false"
                    :value="toTimePickerValue(dateElement.customTime)"
                    placement="top-start"
                    @blur="clearInvalidCustomTime"
                    @update:value="updateCustomTimeFromPicker"
                  ></UITimePicker>
                </div>
                <UISelect
                  v-if="showMeridian"
                  id="customTimeMeridian"
                  class="dt-meridian-select"
                  :value="dateElement.customTimeMeridian || 'AM'"
                  :options="meridianOptions"
                  @update:value="updateCustomTimeMeridian"
                ></UISelect>
              </div>
              <div
                v-if="visibleCustomTimeError"
                class="mt-1 text-xs text-red-500"
              >
                {{ t("common.dateTime.customTimeError") }}
              </div>
            </div>
          </template>

          <UIDivider
            v-if="showDateRangeSettings || showTimeRangeSettings"
          ></UIDivider>

          <!-- Allowed date range (DATE / DATE_TIME) -->
          <template v-if="showDateRangeSettings">
            <div class="form-group">
              <span
                class="switch-handle mb-1 block text-sm font-medium text-gray-700"
                >{{ t("common.dateTime.allowedDateRange") }}</span
              >
              <UISelect
                id="dateMode"
                :value="dateMode"
                :options="dateModeOptions"
                @update:value="updateDateMode"
              ></UISelect>
            </div>

            <!-- future sub-options as radio cards -->
            <div v-if="showFutureSub" class="form-group">
              <span
                class="switch-handle mb-3 block text-sm font-medium text-gray-700"
                >{{ t("common.dateTime.futureQuestion") }}</span
              >
              <UIRadioGroup
                :value="futureSub"
                name="futureSub"
                @update:value="updateFutureSub"
              >
                <div class="flex flex-col gap-4">
                  <UIRadio id="futureAny" value="any_future">
                    <div>
                      <div class="text-sm font-medium text-gray-700">
                        {{ t("common.dateTime.futureAny") }}
                      </div>
                      <div class="mt-0.5 text-xs leading-snug text-gray-500">
                        {{ t("common.dateTime.futureAnyDesc") }}
                      </div>
                    </div>
                  </UIRadio>
                  <UIRadio id="futureBuffer" value="buffer">
                    <div>
                      <div class="text-sm font-medium text-gray-700">
                        {{ t("common.dateTime.futureBuffer") }}
                      </div>
                      <div class="mt-0.5 text-xs leading-snug text-gray-500">
                        {{ t("common.dateTime.futureBufferDesc") }}
                      </div>
                      <div
                        v-if="futureSub === 'buffer'"
                        class="mt-2.5 flex items-center gap-2"
                        @click.stop
                      >
                        <UIInputNumber
                          id="bufferDays"
                          class="w-20"
                          :min="1"
                          :show-button="false"
                          :model-value="toNumber(dateElement.bufferDays)"
                          @update:model-value="updateBuffer"
                          @blur="store.addToHistory()"
                        ></UIInputNumber>
                        <span class="text-sm text-gray-600">{{
                          t("common.dateTime.futureBufferSuffix")
                        }}</span>
                      </div>
                    </div>
                  </UIRadio>
                  <UIRadio id="futureRolling" value="rolling">
                    <div>
                      <div class="text-sm font-medium text-gray-700">
                        {{ t("common.dateTime.futureRolling") }}
                      </div>
                      <div class="mt-0.5 text-xs leading-snug text-gray-500">
                        {{ t("common.dateTime.futureRollingDesc") }}
                      </div>
                      <div
                        v-if="futureSub === 'rolling'"
                        class="mt-2.5 flex items-center gap-2"
                        @click.stop
                      >
                        <span class="text-sm text-gray-600">{{
                          t("common.dateTime.futureRollingPrefix")
                        }}</span>
                        <UIInputNumber
                          id="rollingDays"
                          class="w-20"
                          :min="1"
                          :show-button="false"
                          :model-value="toNumber(dateElement.rollingDays)"
                          @update:model-value="updateRolling"
                          @blur="store.addToHistory()"
                        ></UIInputNumber>
                        <span class="text-sm text-gray-600">{{
                          t("common.dateTime.futureRollingSuffix")
                        }}</span>
                      </div>
                    </div>
                  </UIRadio>
                </div>
              </UIRadioGroup>
            </div>

            <!-- specific date range (side by side) -->
            <div v-if="showDateRangeInputs" class="form-group">
              <span
                class="switch-handle mb-1 block text-sm font-medium text-gray-700"
                >{{ t("common.dateTime.rangeLabel") }}</span
              >
              <div class="flex space-x-2">
                <UIDatepicker
                  id="dateRangeMin"
                  class="flex-1"
                  type="date"
                  :value="toTs(dateElement.dateRangeMin)"
                  @update:value="(ts: number | null) => updateDateRange('min', ts)"
                ></UIDatepicker>
                <UIDatepicker
                  id="dateRangeMax"
                  class="flex-1"
                  type="date"
                  :value="toTs(dateElement.dateRangeMax)"
                  @update:value="(ts: number | null) => updateDateRange('max', ts)"
                ></UIDatepicker>
              </div>
              <div
                v-if="visibleDateRangeError"
                class="mt-1 text-xs text-red-500"
              >
                {{ t("common.dateTime.dateRangeError") }}
              </div>
            </div>

            <!-- available days -->
            <div v-if="showDays" class="form-group">
              <span
                class="switch-handle mb-2 block text-sm font-medium text-gray-700"
                >{{ t("common.dateTime.availableDays") }}</span
              >
              <div class="flex flex-wrap gap-x-4 gap-y-2">
                <UICheckbox
                  v-for="day in weekdayOrder"
                  :id="'day-' + day.key"
                  :key="day.key"
                  :checked="isDayChecked(day.key)"
                  @update:checked="(checked: boolean) => updateDay(day.key, checked)"
                  >{{ day.label }}</UICheckbox
                >
              </div>
              <div v-if="visibleDaysError" class="mt-1 text-xs text-red-500">
                {{ t("common.dateTime.daysError") }}
              </div>
            </div>
          </template>

          <!-- Allowed time range (TIME only) -->
          <template v-if="showTimeRangeSettings">
            <div class="form-group">
              <span
                class="switch-handle mb-1 block text-sm font-medium text-gray-700"
                >{{ t("common.dateTime.allowedTimeRange") }}</span
              >
              <UISelect
                id="timeMode"
                :value="timeMode"
                :options="timeModeOptions"
                @update:value="updateTimeMode"
              ></UISelect>
            </div>
            <div v-if="showTimeRangeInputs" class="form-group">
              <span
                class="switch-handle mb-1 block text-sm font-medium text-gray-700"
                >{{ t("common.dateTime.timeRangeLabel") }}</span
              >
              <div
                class="dt-time-range-row"
                :class="{ 'dt-time-range-row--meridian': showMeridian }"
              >
                <div class="dt-time-range-pair">
                  <span v-if="showMeridian" class="dt-time-range-pair-label">{{
                    t("common.dateTime.timeStart")
                  }}</span>
                  <div class="dt-time-picker-shell dt-time-picker-shell--range">
                    <ClockIcon class="dt-time-picker-leading-icon"></ClockIcon>
                    <UITimePicker
                      id="timeRangeStart"
                      class="standard-input-ghl dt-time-picker"
                      :actions="null"
                      :format="timePickerFormat"
                      :hours="timePickerHours"
                      :placeholder="timePickerPlaceholder"
                      :show-icon="false"
                      :status="visibleTimeRangeError ? 'error' : undefined"
                      :to="false"
                      :value="toTimePickerValue(dateElement.timeRangeStart)"
                      placement="top-start"
                      @blur="() => clearInvalidTimeRange('start')"
                      @update:value="(ts: number | null) => updateTimeRangeFromPicker('start', ts)"
                    ></UITimePicker>
                  </div>
                  <UISelect
                    v-if="showMeridian"
                    id="timeRangeStartMeridian"
                    class="dt-meridian-select"
                    :status="visibleTimeRangeError ? 'error' : undefined"
                    :value="dateElement.timeRangeStartMeridian || 'AM'"
                    :options="meridianOptions"
                    @update:value="(v: string) => updateTimeRangeMeridian('start', v)"
                  ></UISelect>
                </div>
                <span v-if="!showMeridian" class="dt-time-range-separator"
                  >-</span
                >
                <div class="dt-time-range-pair">
                  <span v-if="showMeridian" class="dt-time-range-pair-label">{{
                    t("common.dateTime.timeEnd")
                  }}</span>
                  <div class="dt-time-picker-shell dt-time-picker-shell--range">
                    <ClockIcon class="dt-time-picker-leading-icon"></ClockIcon>
                    <UITimePicker
                      id="timeRangeEnd"
                      class="standard-input-ghl dt-time-picker"
                      :actions="null"
                      :format="timePickerFormat"
                      :hours="timePickerHours"
                      :placeholder="timePickerPlaceholder"
                      :show-icon="false"
                      :status="visibleTimeRangeError ? 'error' : undefined"
                      :to="false"
                      :value="toTimePickerValue(dateElement.timeRangeEnd)"
                      placement="top-start"
                      @blur="() => clearInvalidTimeRange('end')"
                      @update:value="(ts: number | null) => updateTimeRangeFromPicker('end', ts)"
                    ></UITimePicker>
                  </div>
                  <UISelect
                    v-if="showMeridian"
                    id="timeRangeEndMeridian"
                    class="dt-meridian-select"
                    :status="visibleTimeRangeError ? 'error' : undefined"
                    :value="dateElement.timeRangeEndMeridian || 'AM'"
                    :options="meridianOptions"
                    @update:value="(v: string) => updateTimeRangeMeridian('end', v)"
                  ></UISelect>
                </div>
              </div>
              <div
                v-if="visibleTimeRangeError"
                class="mt-1 text-xs text-red-500"
              >
                {{ t("common.dateTime.timeRangeError") }}
              </div>
            </div>
          </template>

          <UIDivider></UIDivider>

          <!-- Disable calendar popup (always) -->
          <div class="form-check-req-hid">
            <UICheckbox
              id="picker-settings"
              :checked="dateElement.disablePicker"
              @update:checked="updateDateDisablePicker"
              >{{ t("common.dateTime.disableCalendarPopup") }}</UICheckbox
            >
          </div>
        </UITabPane>
      </UITabs>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAppStore } from "@/store/app";
import { isCfV1Enabled } from "@/util/customFieldsPhase1";
import {
  DATE_TIME_FORMAT_ORDER,
  WEEKDAY_ORDER,
  separators,
} from "@/util/constants";
import type {
  CollectType,
  DateMode,
  DefaultTimeOption,
  FutureSub,
  Meridian,
  TimeFormat,
  TimeMode,
  WeekdayMap,
} from "@/util/dateTimeSerialize";
import {
  fromTimePickerTimestamp,
  isValidTimeValue,
  to24,
  toInt,
  toTimePickerTimestamp,
} from "@/util/dateTimeSerialize";
import { AlignmentPosition, dateFormatForDayjs } from "@/util/methods";
import {
  CalendarDateIcon,
  ClockIcon,
  InfoCircleIcon,
} from "@gohighlevel/ghl-icons/24/outline";
import {
  UIAlert,
  UIButton,
  UIButtonGroup,
  UICheckbox,
  UIDatepicker,
  UIDivider,
  UIInput,
  UIInputGroup,
  UIInputGroupLabel,
  UIInputNumber,
  UIRadio,
  UIRadioGroup,
  UISelect,
  UITabPane,
  UITabs,
  UITimePicker,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import dayjs from "dayjs";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import LabelAlignment from "./Elements/LabelAlignment.vue";

const { t } = useI18n();
const store = useAppStore();

const props = defineProps({
  index: { type: Number, default: 0 },
  dateElement: {
    type: Object,
    default: () => ({}),
  },
  isRequiredFieldDisable: { type: Boolean, default: false },
  isHiddenFieldDisable: { type: Boolean, default: false },
  getFieldWidthPercentage: {
    type: Function,
    // eslint-disable-next-line no-empty-function
    default: () => {},
  },
  isForm: { type: Boolean, default: false },
  isCustomFieldError: { type: Boolean, default: false },
  disableCollectType: { type: Boolean, default: false },
});

const emit = defineEmits([
  "updateLabel",
  "updatePlaceholder",
  "updateShortLabel",
  "updateHiddenQuery",
  "updateFieldWidthPercentage",
  "updateRequired",
  "updateHidden",
  "updateHiddenValue",
  "updateDateFormat",
  "updateDateSeparator",
  "updateDateDisablePicker",
  "updateLabelAlignment",
  "updateCollectType",
  "updateDateMode",
  "updateFutureSub",
  "updateBuffer",
  "updateRolling",
  "updateDateRange",
  "updateDays",
  "updateTimeMode",
  "updateTimeRange",
  "updateTimeFormat",
  "updateDefaultTime",
  "updateCustomTime",
  "validationError",
]);

// Panel opens on the General tab so the field's label/identity is visible first;
// the design mock is screenshotted on Field settings only to show validation UI.
const activeTab = ref<"general" | "settings">("general");

const selectedFormat = ref(props.dateElement.format || "YYYY-MM-DD");
const selectedSeparator = ref(props.dateElement.separator || "-");

// --- derived collect-type state ---
const collect = computed<CollectType>(
  () => (props.dateElement.dataType as CollectType) || "DATE"
);
const dateMode = computed<DateMode>(
  () => (props.dateElement.dateMode as DateMode) || "any"
);
const futureSub = computed<FutureSub>(
  () => (props.dateElement.futureSub as FutureSub) || "any_future"
);
const timeMode = computed<TimeMode>(
  () => (props.dateElement.timeMode as TimeMode) || "any"
);
const timeFormat = computed<TimeFormat>(
  () => (props.dateElement.timeFormat as TimeFormat) || "24"
);
const defaultTime = computed<DefaultTimeOption>(
  () => (props.dateElement.defaultTime as DefaultTimeOption) || "none"
);

// --- visibility matrix ---
const showDateRange = computed(
  () => collect.value === "DATE" || collect.value === "DATE_TIME"
);
const showTime = computed(
  () => collect.value === "TIME" || collect.value === "DATE_TIME"
);
const showTimeRange = computed(() => collect.value === "TIME");
// Range-restriction sections stay hidden for sub-accounts outside the rollout
// (allowlist or Labs flag); format/separator and time-format controls remain visible.
const newDateTimeEnabled = computed(() => isCfV1Enabled(store.locationId));
const showDateRangeSettings = computed(
  () => newDateTimeEnabled.value && showDateRange.value
);
const showTimeRangeSettings = computed(
  () => newDateTimeEnabled.value && showTimeRange.value
);
const showFutureSub = computed(
  () => showDateRangeSettings.value && dateMode.value === "future_only"
);
const showDateRangeInputs = computed(
  () => showDateRangeSettings.value && dateMode.value === "range"
);
const showDays = computed(
  () =>
    showDateRangeSettings.value &&
    (dateMode.value === "future_only" || dateMode.value === "range")
);
const showTimeRangeInputs = computed(
  () => showTimeRangeSettings.value && timeMode.value === "range"
);
const showMeridian = computed(() => timeFormat.value === "12");

// --- options ---
const collectOptions = computed(() => [
  { label: t("common.dateTime.collectDate"), value: "DATE" },
  { label: t("common.dateTime.collectTime"), value: "TIME" },
  { label: t("common.dateTime.collectDateTime"), value: "DATE_TIME" },
]);
const dateModeOptions = computed(() => [
  { label: t("common.dateTime.dateModeAny"), value: "any" },
  { label: t("common.dateTime.dateModeFuture"), value: "future_only" },
  { label: t("common.dateTime.dateModePast"), value: "past_only" },
  { label: t("common.dateTime.dateModeRange"), value: "range" },
]);
const timeModeOptions = computed(() => [
  { label: t("common.dateTime.timeModeAny"), value: "any" },
  { label: t("common.dateTime.timeModeRange"), value: "range" },
]);
const timeFormatOptions = computed(() => [
  { label: t("common.dateTime.timeFormat24"), value: "24" },
  { label: t("common.dateTime.timeFormat12"), value: "12" },
]);
const defaultTimeOptions = computed(() => [
  { label: t("common.dateTime.defaultTimeNone"), value: "none" },
  { label: t("common.dateTime.defaultTimeCurrent"), value: "current" },
  { label: t("common.dateTime.defaultTimeCustom"), value: "custom" },
]);
const meridianOptions = computed(() => [
  { label: t("common.dateTime.meridianAm"), value: "AM" },
  { label: t("common.dateTime.meridianPm"), value: "PM" },
]);
const timePickerFormat = computed(() => "HH:mm");
const timePickerPlaceholder = computed(() =>
  t("common.dateTime.timePlaceholder").toUpperCase()
);
const timePickerHours = computed(() =>
  timeFormat.value === "12"
    ? Array.from({ length: 12 }, (_, index) => index + 1)
    : undefined
);
const WEEKDAY_LABELS: Record<keyof WeekdayMap, () => string> = {
  Mon: () => t("common.dateTime.weekday.mon"),
  Tue: () => t("common.dateTime.weekday.tue"),
  Wed: () => t("common.dateTime.weekday.wed"),
  Thu: () => t("common.dateTime.weekday.thu"),
  Fri: () => t("common.dateTime.weekday.fri"),
  Sat: () => t("common.dateTime.weekday.sat"),
  Sun: () => t("common.dateTime.weekday.sun"),
};
const weekdayOrder = computed<Array<{ key: keyof WeekdayMap; label: string }>>(
  () =>
    WEEKDAY_ORDER.map((key) => ({
      key: key as keyof WeekdayMap,
      label: WEEKDAY_LABELS[key as keyof WeekdayMap](),
    }))
);

// Date-format segmented label rebuilt live with the chosen separator; the stored
// value stays the canonical dash form so serialization is unaffected.
function formatLabel(format: string): string {
  return dateFormatForDayjs(format, selectedSeparator.value);
}

function toButtonId(prefix: string, value: string): string {
  const special: Record<string, string> = {
    "-": "dash",
    "/": "slash",
    ".": "dot",
  };
  const suffix =
    special[value] ||
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  return `${prefix}-${suffix}`;
}

// --- date <-> timestamp helpers for UIDatepicker ---
function toTs(value: string | undefined): number | null {
  if (!value) return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : null;
}
function toDateStr(ts: number | null): string {
  if (ts === null || ts === undefined) return "";
  const parsed = dayjs(ts);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}
function toNumber(value: number | string | undefined): number {
  return toInt(value);
}
function toTimePickerValue(value: string | undefined): number | null {
  return toTimePickerTimestamp(value, timeFormat.value);
}
function fromTimePickerValue(value: number | null): string | undefined {
  return fromTimePickerTimestamp(value, timeFormat.value);
}

// --- days helpers ---
const daysMap = computed<WeekdayMap>(
  () => (props.dateElement.days as WeekdayMap) || {}
);
function isDayChecked(key: keyof WeekdayMap): boolean {
  return daysMap.value[key] === true;
}

type ValidationTouchKey =
  | "label"
  | "dateRange"
  | "days"
  | "timeRange"
  | "customTime";

const createValidationTouched = (): Record<ValidationTouchKey, boolean> => ({
  label: false,
  dateRange: false,
  days: false,
  timeRange: false,
  customTime: false,
});

const validationTouched = ref<Record<ValidationTouchKey, boolean>>(
  createValidationTouched()
);

function markValidationTouched(...keys: ValidationTouchKey[]): void {
  keys.forEach((key) => {
    validationTouched.value[key] = true;
  });
}

// --- live validation ---
const labelError = computed(
  () => !String(props.dateElement.label ?? "").trim()
);
const daysError = computed(() => {
  if (!showDays.value) return false;
  return WEEKDAY_ORDER.every(
    (k) => daysMap.value[k as keyof WeekdayMap] !== true
  );
});
const dateRangeError = computed(() => {
  if (!showDateRangeInputs.value) return false;
  const min = props.dateElement.dateRangeMin;
  const max = props.dateElement.dateRangeMax;
  if (!min || !max) return true;
  return max < min; // equal allowed (single day)
});
const timeRangeError = computed(() => {
  if (!showTimeRangeInputs.value) return false;
  const start = props.dateElement.timeRangeStart;
  const end = props.dateElement.timeRangeEnd;
  if (!start || !end) return true;
  if (
    !isValidTimeValue(start, timeFormat.value) ||
    !isValidTimeValue(end, timeFormat.value)
  ) {
    return true;
  }
  const s = to24(
    start,
    props.dateElement.timeRangeStartMeridian,
    timeFormat.value
  );
  const e = to24(end, props.dateElement.timeRangeEndMeridian, timeFormat.value);
  return e <= s; // strict: end must be after start, no equal / cross-midnight
});
const customTimeError = computed(
  () =>
    showTime.value &&
    defaultTime.value === "custom" &&
    !isValidTimeValue(props.dateElement.customTime, timeFormat.value)
);
const visibleLabelError = computed(
  () => validationTouched.value.label && labelError.value
);
const visibleDaysError = computed(
  () => validationTouched.value.days && daysError.value
);
const visibleDateRangeError = computed(
  () => validationTouched.value.dateRange && dateRangeError.value
);
const visibleTimeRangeError = computed(
  () => validationTouched.value.timeRange && timeRangeError.value
);
const visibleCustomTimeError = computed(
  () => validationTouched.value.customTime && customTimeError.value
);
const hasValidationError = computed(
  () =>
    visibleLabelError.value ||
    visibleDaysError.value ||
    visibleDateRangeError.value ||
    visibleTimeRangeError.value ||
    visibleCustomTimeError.value
);

watch(hasValidationError, (value) => emit("validationError", value));

// --- emit handlers (store writes happen in the sidebar composable) ---
const updateLabel = (value: string) => {
  markValidationTouched("label");
  emit("updateLabel", value);
};
const handleLabelAlignment = (value: AlignmentPosition) =>
  emit("updateLabelAlignment", value);
const updatePlaceholder = (value: string) => emit("updatePlaceholder", value);
const updateFieldWidthPercentage = (value: string) =>
  emit("updateFieldWidthPercentage", value);
const updateShortLabel = (value: string) => emit("updateShortLabel", value);
const updateHiddenQuery = (value: string) => emit("updateHiddenQuery", value);
const updateRequired = (value: boolean) => emit("updateRequired", value);
const updateHidden = (value: boolean) => emit("updateHidden", value);
const updateHiddenValue = (value: string) => emit("updateHiddenValue", value);

const updateDateFormat = (value: string) => {
  selectedFormat.value = value;
  emit("updateDateFormat", value);
};
const updateDateSeparator = (value: string) => {
  selectedSeparator.value = value;
  emit("updateDateSeparator", value);
};
const updateDateDisablePicker = (value: boolean) =>
  emit("updateDateDisablePicker", value);

const updateCollectType = (value: string) => emit("updateCollectType", value);
const updateDateMode = (value: string) => emit("updateDateMode", value);
const updateFutureSub = (value: string) => emit("updateFutureSub", value);
const updateBuffer = (value: number | null) => emit("updateBuffer", value ?? 0);
const updateRolling = (value: number | null) =>
  emit("updateRolling", value ?? 0);
const updateDateRange = (which: "min" | "max", ts: number | null) => {
  markValidationTouched("dateRange");
  emit("updateDateRange", { which, value: toDateStr(ts) });
};
const updateDay = (key: keyof WeekdayMap, checked: boolean) => {
  markValidationTouched("days");
  emit("updateDays", { key, checked });
};
const updateTimeMode = (value: string) => emit("updateTimeMode", value);
const updateTimeRangeMeridian = (which: "start" | "end", value: string) => {
  markValidationTouched("timeRange");
  emit("updateTimeRange", { which, meridian: value as Meridian });
};
const updateTimeRangeFromPicker = (
  which: "start" | "end",
  value: number | null
) => {
  markValidationTouched("timeRange");
  const timeValue = fromTimePickerValue(value);
  emit(
    "updateTimeRange",
    timeValue === undefined
      ? { which, value: undefined, meridian: undefined }
      : { which, value: timeValue }
  );
};
const updateTimeFormat = (value: string) => emit("updateTimeFormat", value);
const updateDefaultTime = (value: string) => emit("updateDefaultTime", value);
const updateCustomTimeFromPicker = (value: number | null) => {
  markValidationTouched("customTime");
  const timeValue = fromTimePickerValue(value);
  emit(
    "updateCustomTime",
    timeValue === undefined
      ? { value: undefined, meridian: undefined }
      : timeValue
  );
};
const updateCustomTimeMeridian = (value: string) => {
  markValidationTouched("customTime");
  emit("updateCustomTime", { meridian: value as Meridian });
};
const clearInvalidTimeRange = (which: "start" | "end") => {
  markValidationTouched("timeRange");
  const value =
    which === "start"
      ? props.dateElement.timeRangeStart
      : props.dateElement.timeRangeEnd;
  if (value && !isValidTimeValue(value, timeFormat.value)) {
    updateTimeRangeFromPicker(which, null);
  }
};
const clearInvalidCustomTime = () => {
  markValidationTouched("customTime");
  if (
    props.dateElement.customTime &&
    !isValidTimeValue(props.dateElement.customTime, timeFormat.value)
  ) {
    updateCustomTimeFromPicker(null);
  }
};

watch(
  () => props.dateElement.format,
  (newValue) => (selectedFormat.value = newValue || "YYYY-MM-DD")
);
watch(
  () => props.dateElement.separator,
  (newValue) => (selectedSeparator.value = newValue || "-")
);
watch(
  () => props.dateElement,
  () => {
    validationTouched.value = createValidationTouched();
  }
);
</script>

<style scoped>
.date-time-settings-tabs :deep(.n-tabs-wrapper) {
  display: flex;
  width: 100%;
}

.date-time-settings-tabs :deep(.n-tabs-tab-wrapper) {
  flex: 1 1 0;
  min-width: 0;
}

.date-time-settings-tabs :deep(.n-tabs-tab) {
  display: flex;
  justify-content: center;
  width: 100%;
}

.dt-field-settings-banner-alert :deep(.n-alert) {
  border-radius: 12px;
  background-color: var(--gray-25);
}

.dt-field-settings-banner-alert :deep(.n-alert__icon) {
  display: none;
}

.dt-field-settings-banner-alert :deep(.n-alert-body) {
  padding: 16px 20px !important;
}

.dt-field-settings-banner-alert :deep(.n-alert-body__content) {
  color: var(--gray-600);
  font-size: 14px;
  line-height: 20px;
}

.dt-field-settings-banner-alert :deep(.body) {
  margin-right: 0;
}

.dt-field-settings-banner {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.dt-field-settings-banner-icon {
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  color: var(--primary-600);
}

.dt-field-settings-banner-text {
  flex: 1 1 auto;
  min-width: 0;
}

.dt-segmented-control {
  display: flex;
  width: 100%;
}

.dt-segmented-control :deep(.n-button) {
  flex: 1 1 0;
  min-width: 0;
  height: 40px;
  border-radius: 0 !important;
  font-size: 13px;
  font-weight: 600;
  padding-left: 8px;
  padding-right: 8px;
  white-space: nowrap;
}

.dt-segmented-control :deep(.n-button__content) {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  width: 100%;
  text-align: center;
}

.dt-segmented-control :deep(.n-button:first-child) {
  border-bottom-left-radius: 10px !important;
  border-top-left-radius: 10px !important;
}

.dt-segmented-control :deep(.n-button:last-child) {
  border-bottom-right-radius: 10px !important;
  border-top-right-radius: 10px !important;
}

.dt-segmented-control :deep(.dt-segmented-button--active) {
  background-color: var(--primary-50) !important;
  color: var(--primary-700) !important;
}

.dt-segmented-button--active :deep(.n-button__content) {
  color: var(--primary-700) !important;
}

.dt-time-picker-row,
.dt-time-range-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.dt-time-range-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
}

.dt-time-range-row--meridian {
  grid-template-columns: minmax(0, 1fr);
}

.dt-time-range-pair {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  min-width: 0;
}

.dt-time-range-pair-label {
  grid-column: 1 / -1;
  color: var(--gray-700);
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
}

.dt-time-range-row--meridian .dt-time-range-pair {
  grid-template-columns: minmax(0, 1fr) 92px;
}

.dt-time-picker-shell {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
}

.dt-time-picker-shell--range {
  flex-basis: 112px;
  min-width: 112px;
}

.dt-time-range-row--meridian .dt-time-picker-shell--range {
  flex-basis: auto;
  min-width: 0;
}

.dt-time-picker {
  width: 100%;
}

.dt-time-picker-leading-icon {
  position: absolute;
  top: 50%;
  left: 12px;
  z-index: 1;
  height: 18px;
  width: 18px;
  transform: translateY(-50%);
  color: var(--gray-400);
  pointer-events: none;
}

.dt-time-picker :deep(.n-input) {
  min-height: 40px;
  border-radius: 8px;
}

.dt-time-picker :deep(.n-input-wrapper) {
  padding-left: 38px !important;
}

.dt-time-picker :deep(.n-input__input-el) {
  font-size: 14px;
  font-weight: 500;
}

.dt-time-picker :deep(.n-input__placeholder) {
  color: var(--gray-400);
}

.dt-time-range-separator {
  flex: 0 0 auto;
  color: var(--gray-400);
  font-size: 16px;
  font-weight: 600;
}

.dt-meridian-select {
  flex: 0 0 92px;
  width: 92px;
  min-width: 92px;
  max-width: 92px;
}

.dt-meridian-select :deep(.n-base-selection) {
  min-height: 40px;
  border-radius: 8px;
}

.dt-meridian-select :deep(.n-base-selection-label),
.dt-meridian-select :deep(.n-base-selection-input) {
  font-size: 14px;
  font-weight: 500;
}

.date-time-settings-tabs :deep(.n-time-picker-panel) {
  width: 240px;
  min-width: 240px;
  padding: 10px 12px;
  overflow: visible;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.14);
}

.date-time-settings-tabs :deep(.n-time-picker-cols) {
  position: relative;
  height: 188px;
  margin-top: 28px;
  overflow: visible;
  border-top: 1px solid var(--gray-200);
}

.date-time-settings-tabs :deep(.n-time-picker-cols::before),
.date-time-settings-tabs :deep(.n-time-picker-cols::after) {
  position: absolute;
  top: -28px;
  width: 50%;
  color: var(--gray-900);
  font-size: 13px;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
}

.date-time-settings-tabs :deep(.n-time-picker-cols::before) {
  left: 0;
  content: "HH";
}

.date-time-settings-tabs :deep(.n-time-picker-cols::after) {
  right: 0;
  content: "MM";
}

.date-time-settings-tabs :deep(.n-time-picker-col) {
  height: 188px;
  min-width: 96px;
}

.date-time-settings-tabs :deep(.n-time-picker-col + .n-time-picker-col) {
  border-left: 1px solid var(--gray-200);
}

.date-time-settings-tabs :deep(.n-time-picker-col__item) {
  height: 36px;
  font-size: 14px;
  font-weight: 500;
}

.date-time-settings-tabs :deep(.n-time-picker-col__padding) {
  height: 0;
}

.date-time-settings-tabs :deep(.n-time-picker-col__item::before) {
  border-radius: 6px;
}

.date-time-settings-tabs :deep(.n-time-picker-col__item--active::before) {
  border: 1px solid var(--primary-300);
  background-color: var(--primary-50);
}
</style>
